/**
 * Nosto-Recommendations.js
 *
 * Componente genérico para solicitar recomendaciones.
 *
 * Modos de carga:
 * - eager: carga al conectarse.
 * - visible: carga cuando entra en pantalla.
 * - manual: requiere llamar load() o emitir refresh.
 *
 * Este componente no renderiza productos.
 */
class NostoRecommendations extends HTMLElement {
    static visibilityObserver = null;

    constructor() {
        super();

        this.loadingPromise = null;
        this.hasLoaded = false;
        this.isConnectedToEvents = false;

        this.handleRefresh = this.handleRefresh.bind(this);
        this.handleActivate = this.handleActivate.bind(this);
    }

    connectedCallback() {
        if (!this.isConnectedToEvents) {
            this.addEventListener(
                "nosto:recommendations:refresh",
                this.handleRefresh
            );

            this.addEventListener(
                "nosto:recommendations:activate",
                this.handleActivate
            );

            this.isConnectedToEvents = true;
        }

        this.initializeLoading();
    }

    disconnectedCallback() {
        this.removeEventListener(
            "nosto:recommendations:refresh",
            this.handleRefresh
        );

        this.removeEventListener(
            "nosto:recommendations:activate",
            this.handleActivate
        );

        NostoRecommendations
            .getVisibilityObserver()
            .unobserve(this);

        this.isConnectedToEvents = false;
    }

    get placementId() {
        return (
            this.dataset.placementId ??
            this.getAttribute("placement-id") ??
            ""
        ).trim();
    }

    get productId() {
        return String(
            this.dataset.productId ?? ""
        ).trim();
    }

    get limit() {
        const value = Number.parseInt(
            this.dataset.limit ?? "",
            10
        );

        return Number.isFinite(value) && value > 0
            ? value
            : null;
    }

    /**
     * eager | visible | manual
     *
     * Compatibilidad:
     * data-autoload="false" equivale a manual.
     * data-autoload="true" equivale a eager.
     */
    get loadingMode() {
        const value = String(
            this.dataset.autoload ?? "eager"
        )
            .trim()
            .toLowerCase();

        if (value === "false" || value === "manual") {
            return "manual";
        }

        if (value === "visible" || value === "lazy") {
            return "visible";
        }

        return "eager";
    }

    initializeLoading() {
        if (this.hasLoaded || this.loadingPromise) {
            return;
        }

        switch (this.loadingMode) {
            case "manual":
                break;

            case "visible":
                NostoRecommendations
                    .getVisibilityObserver()
                    .observe(this);
                break;

            case "eager":
            default:
                this.load();
                break;
        }
    }

    async load({
        force = false,
    } = {}) {
        if (!this.placementId) {
            this.setError(
                new Error(
                    "No se configuró placement-id."
                )
            );

            return null;
        }

        if (this.loadingPromise) {
            return this.loadingPromise;
        }

        if (this.hasLoaded && !force) {
            return this.lastResult ?? null;
        }

        this.loadingPromise =
            this.performLoad({ force });

        try {
            return await this.loadingPromise;
        } finally {
            this.loadingPromise = null;
        }
    }

    async performLoad({
        force,
    }) {
        this.setLoading(true);
        this.clearError();

        try {
            const result =
                await window
                    .NostoRecommendationClient
                    .getRecommendation({
                        placementId:
                            this.placementId,
                        productId:
                            this.productId,
                        force,
                    });

            const allRecommendedProducts =
                result.recommendedProducts ?? [];

            const recommendedProducts =
                this.limit
                    ? allRecommendedProducts.slice(
                        0,
                        this.limit
                    )
                    : allRecommendedProducts;

            const detail = {
                ...result,

                allRecommendedProducts,

                recommendedProducts,

                displayedProducts:
                    recommendedProducts.length,
            };

            this.hasLoaded = true;
            this.lastResult = detail;

            this.dataset.loaded = "true";
            this.dataset.empty = String(
                detail.isEmpty
            );

            /*
             * Después de la primera carga ya no necesitamos
             * observar el componente.
             */
            NostoRecommendations
                .getVisibilityObserver()
                .unobserve(this);

            this.dispatchEvent(
                new CustomEvent(
                    "nosto:recommendations:loaded",
                    {
                        bubbles: true,
                        detail,
                    }
                )
            );

            return detail;
        } catch (error) {
            this.setError(error);
            return null;
        } finally {
            this.setLoading(false);
        }
    }

    handleRefresh(event) {
        /*
         * Evitamos que el mismo componente procese otra vez
         * su propio evento cuando este sube por el DOM.
         */
        if (event.target !== this) {
            return;
        }

        event.stopPropagation();

        this.load({
            force: true,
        });
    }

    handleActivate(event) {
        if (event.target !== this) {
            return;
        }

        event.stopPropagation();

        this.load();
    }

    setLoading(isLoading) {
        this.toggleAttribute(
            "aria-busy",
            isLoading
        );

        this.dataset.loading =
            String(isLoading);

        this.dispatchEvent(
            new CustomEvent(
                isLoading
                    ? "nosto:recommendations:loading"
                    : "nosto:recommendations:load-end",
                {
                    bubbles: true,
                    detail: {
                        placementId:
                            this.placementId,
                    },
                }
            )
        );
    }

    setError(error) {
        this.dataset.error = "true";

        console.error(
            "[NostoRecommendations]",
            error
        );

        this.dispatchEvent(
            new CustomEvent(
                "nosto:recommendations:error",
                {
                    bubbles: true,
                    detail: {
                        placementId:
                            this.placementId,
                        error,
                    },
                }
            )
        );
    }

    clearError() {
        delete this.dataset.error;
    }

    /**
     * Comparte un único IntersectionObserver entre todos
     * los placements de la página.
     */
    static getVisibilityObserver() {
        if (this.visibilityObserver) {
            return this.visibilityObserver;
        }

        this.visibilityObserver =
            new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (!entry.isIntersecting) {
                            return;
                        }

                        const component = entry.target;

                        this.visibilityObserver
                            .unobserve(component);

                        component.load();
                    });
                },
                {
                    /*
                     * Empieza a cargar poco antes de que el componente
                     * sea visible para el usuario.
                     */
                    rootMargin: "300px 0px",
                    threshold: 0.01,
                }
            );

        return this.visibilityObserver;
    }

    /**
     * Carga todos los placements contenidos en root.
     */
    static loadAll(
        root = document,
        {
            force = false,
        } = {}
    ) {
        const components = [];

        if (
            root instanceof Element &&
            root.matches("nosto-recommendations")
        ) {
            components.push(root);
        }

        components.push(
            ...root.querySelectorAll(
                "nosto-recommendations"
            )
        );

        return Promise.all(
            components.map((component) =>
                component.load({ force })
            )
        );
    }

    /**
     * Activa placements de un panel recién mostrado.
     *
     * Los placements manuales se cargan inmediatamente.
     * Los visibles se vuelven a observar para que carguen
     * cuando el panel tenga dimensiones reales.
     */
    static activate(root) {
        if (!root) {
            return Promise.resolve([]);
        }

        const components = [];

        if (
            root instanceof Element &&
            root.matches("nosto-recommendations")
        ) {
            components.push(root);
        }

        components.push(
            ...root.querySelectorAll(
                "nosto-recommendations"
            )
        );

        const loads = components.map((component) => {
            if (
                component.hasLoaded ||
                component.loadingPromise
            ) {
                component.dispatchEvent(
                    new CustomEvent(
                        "nosto:recommendations:shown",
                        {
                            bubbles: true,
                            detail: component.lastResult,
                        }
                    )
                );

                return Promise.resolve(
                    component.lastResult ?? null
                );
            }

            if (
                component.loadingMode === "visible"
            ) {
                this.getVisibilityObserver()
                    .observe(component);

                return Promise.resolve(null);
            }

            return component.load();
        });

        return Promise.all(loads);
    }
}

if (
    !customElements.get(
        "nosto-recommendations"
    )
) {
    customElements.define(
        "nosto-recommendations",
        NostoRecommendations
    );
}

window.NostoRecommendations =
    NostoRecommendations;